import React, { useState, useEffect } from 'react';
import {
  FaVideo, FaInfinity, FaCertificate,
  FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../../components/ui/notify';
import { Icon } from '@iconify/react';
import courseImg from '../../../assets/images/course/course1.png';

import DefaultProfile from '../../../assets/images/profile/default-profile.png';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import { getMetadataCourseDetails, AddMyCourses, verifyUserCourseAccess } from '../../../services/user/course';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import ContentUnavailable from '../../../components/common/ContentUnavailable';
import { isNotFoundError } from '../../../utils/isNotFoundError';
import { useUserCourse } from '../../../contexts/UserCourseContext';

// Helper function to get currency symbol
const getCurrencySymbol = (currency) => {
  switch (currency?.toUpperCase()) {
    case 'INR':
      return '₹';
    case 'USD':
      return '$';
    case 'SGD':
      return 'S$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    default:
      return '$'; // Default to USD symbol
  }
};

const isFreeCourse = (course) => {
  if (!course) return false;
  const courseType = course.course_type?.toLowerCase();
  if (courseType === 'paid') return false;
  if (courseType === 'free') return true;
  return Number(course.course_price || 0) <= 0;
};

function CourseDetails() {


  const navigate = useNavigate();
  const { setCourseName } = useUserCourse();
  const [expandedModules, setExpandedModules] = useState(['module1']);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isPaidEnrolling, setIsPaidEnrolling] = useState(false);


  const { encodedId } = useParams();
  const decoded = decodeData(encodedId);
  const courseId = decoded?.id;

  const [courseData, setCourseData] = useState(null);
  const [courseStatus, setCourseStatus] = useState('loading');
  const [unavailableMessage, setUnavailableMessage] = useState('');

  // Function to process course data and parse JSON fields
  const processCourseData = (data) => {
    if (!data) return data;

    const processed = { ...data };

    // Parse tags if it's a string
    if (typeof processed.tags === 'string') {
      try {
        processed.tags = JSON.parse(processed.tags);
      } catch (error) {
        processed.tags = [];
      }
    }

    if (typeof processed.course_info === 'string') {
      try {
        processed.course_info = JSON.parse(processed.course_info);
      } catch (error) {
        processed.course_info = [];
      }
    }

    return processed;
  };

  useEffect(() => {
    setCourseName('');

    if (!courseId) {
      setCourseStatus('unavailable');
      setUnavailableMessage('Invalid course ID');
      return;
    }

    let cancelled = false;

    const verifyAndLoad = async () => {
      setCourseStatus('loading');
      try {
        const response = await verifyUserCourseAccess({
          course_id: courseId,
          mode: 'details',
        });
        if (cancelled) return;
        if (response?.success) {
          setCourseStatus('available');
          await getCourseDetails();
          return;
        }
        setCourseStatus('unavailable');
        setUnavailableMessage('This course is not available');
      } catch (verifyError) {
        if (cancelled) return;
        setUnavailableMessage(
          verifyError?.message?.includes('access')
            ? 'You no longer have access to this course'
            : 'This course is not available'
        );
        setCourseStatus('unavailable');
      }
    };

    verifyAndLoad();

    return () => {
      cancelled = true;
    };
  }, [courseId, setCourseName]);

  async function getCourseDetails() {
    try {
      const response = await getMetadataCourseDetails({
        course_id: courseId
      });
      const processedData = processCourseData(response.data);
      setCourseData(processedData);
      setCourseName(processedData?.course_name || '');
    } catch (error) {
      if (isNotFoundError(error)) {
        setCourseStatus('unavailable');
        setUnavailableMessage('This course is not available');
      }
    }
  }

  async function EnrollFreeCourse() {
    try {
      setIsEnrolling(true);

      await AddMyCourses({
        course_id: courseId
      });

      await new Promise(resolve => setTimeout(resolve, 2000));

      // Show toast after loading
      toast.success('Enrolled successfully! Redirecting...', {
        position: 'top-right',
        autoClose: 1000,
      });

      // Wait 1 second after toast before redirecting
      await new Promise(resolve => setTimeout(resolve, 1000));

      const encodedCourseId = encodeData({ id: courseId });
      navigate(`/user/courses/WatchCourse/${encodeURIComponent(encodedCourseId)}`);

    } catch (error) {
      toast.error('Failed to enroll in course. Please try again.');
    } finally {
      setIsEnrolling(false);
    }
  }

  const toggleModule = (moduleId) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handlePaidEnrollment = async () => {
    setIsPaidEnrolling(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    const targetState = {
    course_id: Number(courseId),
    course_name: courseData?.course_name || '',
    course_price: courseData?.course_price || '0',
    currency: courseData?.currency || 'USD',
    banner_image: courseData?.banner_image || '',
    course_type: courseData?.course_type || 'Paid',
    course_desc: courseData?.course_desc || '',
    discountCode: null,
    discountValue: 0,
    points: 0
  };
    // PayU only
    navigate('/user/courses/orderDetails', { state: targetState });

    setIsPaidEnrolling(false);
  };

  if (courseStatus === 'unavailable') {
    return (
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <ContentUnavailable
            title={unavailableMessage || 'This course is not available'}
            description="It may have been removed, deleted, or you no longer have access to it."
            icon="mdi:book-remove-outline"
            backLabel="Back to Courses"
            backTo="/user/courses"
          />
        </div>
      </div>
    );
  }

  if (courseStatus === 'loading' || !courseData) {
    return <Loader variant="page" caption="Loading course..." />;
  }

  const hasModules = courseData.modules &&
    Object.keys(courseData.modules).length > 0;

  return (
    <>
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full tw-p-0">
          <div className={"[background:linear-gradient(to_right,_#FF69B4,_#FF4500)] tw-text-[white] tw-p-[4rem_0] tw-mb-8 [&_h3]:tw-text-[2.8rem] [&_h3]:tw-font-bold [&_h3]:tw-mb-4 [&_h3]:tw-text-[#fff] [&_.font-size-8]:tw-text-[1.2rem] [&_.font-size-8]:tw-opacity-[0.95] [&_.font-size-8]:tw-mb-8 [&_.font-size-8]:tw-font-medium [&_.iconify]:tw-text-[1.4rem] [&_.iconify]:tw-align-middle [&_.iconify]:tw-mr-2 [&_.tw-flex.tw-items-center]:tw-text-[1rem] [&_.tw-flex.tw-items-center]:tw-text-[rgba(255,_255,_255,_0.9)] [&_.tw-text-amber-600]:!tw-text-[#FFD700] [&_.breadcrumb]:tw-bg-[transparent] [&_.breadcrumb-item_a]:tw-text-[rgba(255,_255,_255,_0.8)] [&_.breadcrumb-item_a]:tw-no-underline [&_.breadcrumb-item.active]:tw-text-[white] [&_.tw-text-slate-500]:!tw-text-[rgba(255,_255,_255,_0.9)] [&_.lead]:!tw-text-[rgba(255,_255,_255,_0.9)] [&_.rating-stars]:tw-text-[#FFD700] [@mediascreen_and_(max-width:_768px)]:tw-p-[2rem_0] [@mediascreen_and_(max-width:_768px)]:[&_h3]:tw-text-[2rem] course-details-header"}>
            <div className="tw-ml-auto tw-mr-auto tw-w-full tw-max-w-[80rem] tw-pl-4 tw-pr-4 ui-container">
            <h3
  className="tw-font-bold tw-mb-4"
  style={{
    wordWrap: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "normal",
  }}
>
  {courseData.course_name || "Untitled Course"}
</h3>
              <p className="font-size-8">
                {courseData.tags && Array.isArray(courseData.tags) ? (
                  courseData.tags.join(' | ')
                ) : (
                  'No tags available'
                )}
              </p>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-6 tw-mb-4">
                <div className="tw-flex tw-items-center">
                  <Icon icon="material-symbols:star" className="!tw-text-[var(--warning-color)] tw-text-amber-600" />
                  <span className="tw-ms-2">{courseData?.courseMeta?.averageRating || '0.0'} rating</span>
                </div>
                <div className="tw-flex tw-items-center">
                  <Icon icon="material-symbols:menu-book" className="tw-me-2" />
                  <span>{courseData?.courseMeta?.modulesCount || 0} Modules</span>
                </div>
                {/* <div className="d-flex align-items-center">
                  <Icon icon="material-symbols:group" className="me-2" />
                  <span>{courseData?.courseMeta?.enrolledCount || 0} Students</span>
                </div> */}
                <div className="tw-flex tw-items-center">
                  <Icon icon="material-symbols:school" className="tw-me-2" />
                  <span>Level: {courseData?.levels || 'Not specified'}</span>
                </div>
                <div className="tw-flex tw-items-center">
                  <Icon icon="material-symbols:language" className="tw-me-2" />
                  <span>Language: {courseData?.course_language || 'Not specified'}</span>
                </div>
              </div>
              <div className="instructor tw-flex tw-items-center tw-gap-2">
                <div className={"tw-rounded-[50%] tw-overflow-hidden tw-shadow-[0_2px_8px_rgba(0,_0,_0,_0.15)] tw-border-[2px] tw-border-solid tw-border-[rgba(255,_255,_255,_0.8)] [&_img]:tw-block instructor-image"} style={{ width: '40px', height: '40px', minWidth: '40px' }}>
                  <img
                    src={courseData?.trainer?.profile || DefaultProfile}
                    alt={courseData?.trainer?.name}
                    className="tw-rounded-full"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div className="tw-flex tw-flex-col">
                  <span className={"tw-text-[0.95rem] tw-text-[rgba(255,_255,_255,_0.9)] [&:first-child]:tw-font-semibold font-size-1"}>By {courseData?.trainer?.name || 'Loading...'}</span>
                  <span className={"tw-text-[0.95rem] tw-text-[rgba(255,_255,_255,_0.9)] [&:first-child]:tw-font-semibold font-size-1"}>{courseData?.trainer?.role || 'Instructor'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-justify-center"}>
            <div className="tw-min-w-[0px] ui-column tw-w-full md:tw-w-11/12">
              <div className="tw-bg-[white] tw-rounded-[8px] tw-mt-[-60px] tw-p-8 tw-shadow-[0_0_20px_rgba(0,_0,_0,_0.1)] [@mediascreen_and_(max-width:_768px)]:tw-p-4 course-details-container">
                <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
                  <div className="tw-min-w-[0px] ui-column lg:tw-w-2/3">
                    <div className="what-youll-learn tw-mb-6">
                      <h3 className="h4 tw-mb-4">Course Info</h3>
                      <div className="tw-bg-[#f8f9fa] tw-rounded-[12px] tw-p-6 learning-points">
                        {courseData.course_info && Array.isArray(courseData.course_info) ? (
                          courseData.course_info.map((point, index) => (
                            <div key={index} className={"[transition:all_0.2s_ease] tw-p-[0.5rem_1rem] tw-rounded-[8px] [&_.iconify]:tw-text-[1.25rem] [&_.iconify]:tw-text-[#28a745] [&_span]:tw-text-[#2c3e50] [&_span]:tw-text-[1rem] [&_span]:tw-leading-[1.5] learning-point tw-mb-4 tw-flex tw-items-center"}>
                              <Icon icon="material-symbols:check-circle" className="!tw-text-[var(--success-color)] tw-text-emerald-600 tw-me-4" />
                              <span>{point}</span>
                            </div>
                          ))
                        ) : (
                          <div className={"[transition:all_0.2s_ease] tw-p-[0.5rem_1rem] tw-rounded-[8px] [&_.iconify]:tw-text-[1.25rem] [&_.iconify]:tw-text-[#28a745] [&_span]:tw-text-[#2c3e50] [&_span]:tw-text-[1rem] [&_span]:tw-leading-[1.5] learning-point tw-mb-4 tw-flex tw-items-center"}>
                            <Icon icon="material-symbols:check-circle" className="!tw-text-[var(--success-color)] tw-text-emerald-600 tw-me-4" />
                            <span>Course information not available</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {courseData.course_desc ? (
                      <div className={"tw-border-t-[1px] tw-border-solid tw-border-t-[#e9ecef] tw-pt-8 [&_h3]:tw-text-[#2c3e50] [&_h3]:tw-font-semibold about-course tw-mb-6"}>
                        <h3 className="h4 tw-mb-4">About This Course</h3>
                        <div className={"tw-text-[#6c757d] tw-text-[1rem] tw-leading-[1.7] [&_p]:tw-mb-6 about-content"}>
                          <p
                            className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-4"
                            style={{
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                            }}
                          >
                            {courseData.course_desc}
                          </p>
                        </div>
                      </div>
                    ) : null}


                    <div className={"tw-mt-8 tw-p-[24px] tw-bg-[white] tw-rounded-[12px] tw-overflow-y-auto [&_h2]:tw-text-[24px] [&_h2]:tw-mb-[10px] [&_h2]:tw-text-[#333] [&_.content-item:hover]:!tw-bg-[#eef1ff] [&_.content-item.active]:!tw-bg-[#eef1ff] [&_.content-item.active]:tw-border-l-[4px] [&_.content-item.active]:tw-border-solid [&_.content-item.active]:tw-border-l-[#3152e8] [&_.content-item.active:hover]:!tw-bg-[#eef1ff] [&_.content-item.active:hover]:tw-border-l-[4px] [&_.content-item.active:hover]:tw-border-solid [&_.content-item.active:hover]:tw-border-l-[#3152e8] [&_.content-item.locked:hover]:!tw-bg-[#f1f5f9] max-[768px]:tw-p-[15px] course-content tw-mb-6 tw-shadow-none"}>
                      <h3 className="h4 tw-mb-4">Course Content</h3>
                      {hasModules ? (
                        <div className={"tw-flex tw-flex-col tw-gap-[15px] tw-mt-[20px] tw-max-h-[calc(100vh_-_10px)] tw-overflow-y-auto tw-pr-4 [&::-webkit-scrollbar]:tw-w-[6px] [&::-webkit-scrollbar-track]:tw-bg-[#f1f1f1] [&::-webkit-scrollbar-track]:tw-rounded-[3px] [&::-webkit-scrollbar-thumb]:tw-bg-[#888] [&::-webkit-scrollbar-thumb]:tw-rounded-[3px] [&::-webkit-scrollbar-thumb:hover]:tw-bg-[#555] modules-list"}>
                          {Object.entries(courseData.modules).map(([moduleName, contents]) => (
                            <div key={moduleName} className="tw-border-[1px] tw-border-solid tw-border-[#e9ecef] tw-rounded-[10px] tw-mb-[5px] tw-overflow-hidden tw-shadow-[0_2px_8px_rgba(0,_0,_0,_0.05)] module-item">
                              <div
                                className={"[&_svg]:tw-text-[#6c757d] [&_svg]:tw-text-[0.875rem] [&:hover]:tw-bg-[#f0f0f0] [&.expanded]:tw-bg-[#3152e8] [&.expanded]:tw-text-[white] [&.expanded]:tw-border-l-[4px] [&.expanded]:tw-border-solid [&.expanded]:tw-border-l-[#1e3ad2] tw-bg-[#f8f9fa] [transition:all_0.2s_ease] tw-cursor-pointer tw-border-l-[4px] tw-border-solid tw-border-l-[transparent] tw-rounded-[8px_8px_0_0] [&.active]:tw-bg-[#e9ecef] [&.active]:tw-border-b-[1px] [&.active]:tw-border-solid [&.active]:tw-border-b-[#e0e0e0] module-header tw-flex tw-items-center tw-justify-between tw-p-4"}
                                onClick={() => toggleModule(moduleName)}
                                style={{ cursor: 'pointer' }}
                              >
                                <div className="tw-flex tw-items-center">
                                  {expandedModules.includes(moduleName) ? <FaChevronDown /> : <FaChevronUp />}
                                  <span className="tw-ms-2">{moduleName}</span>
                                </div>
                                <div className="tw-text-[0.875rem] !tw-text-[var(--secondary-color)] module-meta tw-text-slate-500">
                                  <small>{contents.length} items</small>
                                </div>
                              </div>
                              {expandedModules.includes(moduleName) && (
                                <div className={"tw-p-0 tw-bg-[white] tw-max-h-[60vh] tw-min-h-[0] tw-overflow-y-auto [-webkit-overflow-scrolling:touch] [scrollbar-width:thin] [scrollbar-color:#3152e8_#f0f0f0] [&::-webkit-scrollbar]:tw-w-[8px] [&::-webkit-scrollbar-track]:tw-bg-[#f0f0f0] [&::-webkit-scrollbar-track]:tw-rounded-[4px] [&::-webkit-scrollbar-thumb]:tw-bg-[#3152e8] [&::-webkit-scrollbar-thumb]:tw-rounded-[4px] [&::-webkit-scrollbar-thumb:hover]:tw-bg-[#1e3ad2] [&_.content-item:hover]:!tw-bg-[#eef1ff] [&_.content-item.locked:hover]:!tw-bg-[#f1f5f9] max-[768px]:tw-max-h-[none] max-[768px]:tw-overflow-visible module-content"}>
                                  {contents.map((content, idx) => (
                                    <div key={idx} className={"tw-border-t-[1px] tw-border-solid tw-border-t-[#e9ecef] [transition:background-color_0.2s_ease] [&:hover]:tw-bg-[#f8f9fa] [&_svg]:tw-text-[#6c757d] [&_svg]:tw-text-[1.25rem] lecture-item tw-flex tw-items-center tw-justify-between tw-p-4"}>
                                      <div className="tw-flex tw-items-center">
                                        {content.type === 'Video' && (
                                          <Icon icon="material-symbols:play-circle-outline" className="tw-me-2" />
                                        )}
                                        {content.type === 'Document' && (
                                          <Icon icon="material-symbols:description-outline" className="tw-me-2" />
                                        )}
                                        {content.type === 'Assessment' && (
                                          <Icon icon="material-symbols:quiz-outline" className="tw-me-2" />
                                        )}
                                        {content.type === 'Survey' && (
                                          <Icon icon="material-symbols:analytics-outline" className="tw-me-2" />
                                        )}
                                        <span>{content.title}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                            </div>
                          ))}
                        </div>
                      ) : (
                        <NoData caption="No course content available yet" />
                      )}
                    </div>

                    {courseData.trainer ? (
                      <div className="tw-bg-[#fff] tw-rounded-[12px] tw-p-8 [@mediascreen_and_(max-width:_768px)]:tw-p-4 instructor-profile tw-mb-6">
                        <h3 className="h4 tw-mb-6">Instructor</h3>
                        <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-6">

                          {/* Image Wrapper */}
                          <div
                            className={"[&_img]:tw-rounded-[12px] [&_img]:tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.1)] [@mediascreen_and_(max-width:_768px)]:[&_img]:!tw-w-[150px] [@mediascreen_and_(max-width:_768px)]:[&_img]:!tw-h-[150px] [@mediascreen_and_(max-width:_768px)]:[&_img]:tw-m-[0_auto] [@mediascreen_and_(max-width:_768px)]:[&_img]:tw-block instructor-image-lg tw-hidden md:tw-block"}
                            style={{
                              width: '150px',
                              height: '150px',
                              minWidth: '150px',
                              overflow: 'hidden',
                              borderRadius: '12px',
                              position: 'relative',
                              backgroundColor: '#f8f9fa'
                            }}
                          >
                            <img
                              src={courseData?.trainer?.profile || DefaultProfile}
                              alt={courseData?.trainer?.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </div>

                          {/* Instructor Info */}
                          <div className={"[&_h4]:tw-text-[#2c3e50] [&_h4]:tw-mb-2 [&_.rating-stars]:tw-inline-flex [&_.rating-stars]:tw-gap-[2px] [&_.rating-stars_.iconify]:tw-text-[1.2rem] [&_.rating-stars_.iconify]:tw-text-[#FFD700] [@mediascreen_and_(max-width:_768px)]:tw-text-center [@mediascreen_and_(max-width:_768px)]:[&_.tw-flex]:tw-justify-center [@mediascreen_and_(max-width:_576px)]:[&_.tw-flex.tw-flex-wrap]:tw-flex-col [@mediascreen_and_(max-width:_576px)]:[&_.tw-flex.tw-flex-wrap]:tw-items-center [@mediascreen_and_(max-width:_576px)]:[&_.tw-flex.tw-flex-wrap]:!tw-gap-4 instructor-info"}>
                            <h4 className="h5 tw-mb-1" style={{textAlign: 'left'}}>{courseData?.trainer?.name || 'Loading...'}</h4>
                            <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-4" style={{textAlign: 'left'}}>
                              {courseData?.trainer?.role || 'Fire Management Specialist & Environmental Trainer'}
                            </p>

                            {/* <div className="d-flex flex-wrap align-items-center gap-4 mb-3"> */}

                            {/* <div className="d-flex align-items-center">
                                <span className="me-2">4.5</span>
                                <div className="rating-stars">
                                  <Icon icon="material-symbols:star" className="text-warning" />
                                  <Icon icon="material-symbols:star" className="text-warning" />
                                  <Icon icon="material-symbols:star" className="text-warning" />
                                  <Icon icon="material-symbols:star" className="text-warning" />
                                  <Icon icon="material-symbols:star-half" className="text-warning" />
                                </div>
                                <span className="ms-2">6 Reviews</span>
                              </div> */}
                            {/*
                              <div className="d-flex align-items-center">
                                <Icon icon="material-symbols:group" className="me-2" />
                                <span>0 Students</span>
                              </div>

                              <div className="d-flex align-items-center">
                                <Icon icon="material-symbols:play-circle" className="me-2" />
                                <span>0 Courses</span>
                              </div> */}
                            {/* </div> */}

                            <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-6" style={{textAlign: 'left'}}>
                              {courseData?.trainer?.bio || 'No bio available'}
                            </p>
                          </div>
                        </div>
                      </div>


                    ) : (
                      <NoData caption="Instructor information not available" />
                    )}
                  </div>

                  <div className="tw-min-w-[0px] ui-column lg:tw-w-1/3 tw-mt-12 lg:tw-mt-0">
                    <div className={"[&_.ui-card-img-top]:tw-rounded-[12px_12px_0_0] tw-h-full tw-overflow-hidden [&:hover]:[transform:translateY(-5px)] [&:hover]:tw-shadow-[0_10px_20px_rgba(0,_0,_0,_0.1)] [&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] course-card ui-card tw-shadow-sm"} style={{ position: 'relative', top: '-5rem' }}>
                      <img
                        src={courseData.banner_image || courseImg}
                        className="tw-block tw-w-[calc(100%-1rem)] tw-aspect-video tw-object-cover tw-rounded-xl tw-border-2 tw-m-2"
                        alt={courseData.course_name || "Course Preview"}
                        onError={(e) => {
                          e.target.src = courseImg;
                          e.target.onerror = null;
                        }}
                      />
                      <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
                        {!isFreeCourse(courseData) && (
                          <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                            <span className="tw-text-[2.5rem] tw-font-bold tw-text-[#FF4500] price-tag">
                              {getCurrencySymbol(courseData?.currency)} {courseData?.course_price || '0'}
                            </span>
                            <span className="[text-decoration:line-through] tw-text-[#6c757d] tw-text-[1.1rem] original-price">
                              {getCurrencySymbol(courseData?.currency)}
                              {Number(courseData?.course_price || 0) + 5}
                            </span>
                          </div>
                        )}

                        {!isFreeCourse(courseData) ? (
                          <button
                            onClick={handlePaidEnrollment}
                            className={"tw-w-full tw-p-[12px] tw-rounded-[8px] tw-font-medium tw-flex tw-items-center tw-justify-center tw-gap-[8px] [transition:all_0.3s_ease] tw-mb-4 tw-bg-[#4169E1] tw-text-[white] tw-border-0 [&:hover]:tw-bg-[#3457b1] [&:hover]:[transform:translateY(-2px)] paid-btn"}
                            disabled={isPaidEnrolling}
                          >
                            {isPaidEnrolling ? (
                              <>
                                <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" role="status" aria-hidden="true"></span>
                                Processing...
                              </>
                            ) : (
                              <>
                                <Icon
                                  icon="mdi:lock"
                                  className={"[[data-theme='dark']_&:hover]:tw-bg-[var(--bg-tertiary)] tw-text-[20px] btn-icon"}
                                />
                                Enroll Now
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            className={"[&.free-btn]:tw-bg-[#22c55e] [&.free-btn]:tw-text-[white] [&.free-btn:hover]:tw-bg-[#1ea550] [&.free-btn:hover]:tw-text-[white] [&.paid-btn]:tw-bg-[#4361ee] [&.paid-btn]:tw-text-[white] [&.paid-btn:hover]:tw-bg-[#2541df] [&.paid-btn:hover]:tw-text-[white] [&_.btn-icon]:tw-text-[1.25rem] [&_.btn-icon]:!tw-text-[white] [&_.btn-icon]:!tw-bg-[transparent] [&_.btn-icon]:!tw-p-0 [&_.btn-icon]:!tw-rounded-[0] [&_.btn-icon]:tw-flex [&_.btn-icon]:tw-items-center [&_.btn-icon]:tw-justify-center [&_.btn-icon_svg]:!tw-bg-[transparent] [&_.btn-icon_svg]:![fill:white] [&_.btn-icon_svg]:!tw-rounded-[0] [&_.btn-icon_svg]:!tw-p-0 [&:hover_.btn-icon]:!tw-text-[white] [&:hover_.btn-icon]:![fill:white] [&:hover_.btn-icon]:!tw-bg-[transparent] [&:hover_.btn-icon_svg]:!tw-text-[white] [&:hover_.btn-icon_svg]:![fill:white] [&:hover_.btn-icon_svg]:!tw-bg-[transparent] tw-flex tw-items-center tw-justify-center tw-gap-[8px] tw-p-[12px] tw-rounded-[8px] tw-text-[0.875rem] tw-font-medium tw-cursor-pointer [transition:all_0.3s_ease] tw-text-center tw-whitespace-nowrap tw-leading-[1] tw-min-w-[auto] tw-w-full tw-mb-4 tw-bg-[#4CAF50] tw-text-[white] tw-border-0 [&:hover]:tw-bg-[#45a049] [&:hover]:[transform:translateY(-2px)] watch-now-btn free-btn"}
                            onClick={courseData.is_course_purchase ? () => {
                              const encodedCourseId = encodeData({ id: courseId });
                              navigate(`/user/courses/WatchCourse/${encodeURIComponent(encodedCourseId)}`);
                            } : EnrollFreeCourse}
                            disabled={isEnrolling}
                          >
                            {isEnrolling ? (
                              <>
                                <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" role="status" aria-hidden="true"></span>
                                Enrolling...
                              </>
                            ) : (
                              <>
                                <Icon
                                  icon="mdi:play-circle"
                                  className={"[[data-theme='dark']_&:hover]:tw-bg-[var(--bg-tertiary)] tw-text-[20px] btn-icon"}
                                />
                                {courseData.is_course_purchase ? 'Continue Learning' : 'Watch Now'}
                              </>
                            )}
                          </button>
                        )}


                        <div className="tw-flex tw-justify-center tw-items-center">
                          <FaInfinity className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-me-2" />
                          Full lifetime access
                        </div>

                        <div className={"tw-bg-[#f8f9fa] tw-p-6 tw-rounded-[12px] tw-m-[1.5rem_0] [&_h4]:tw-text-[#2c3e50] [&_h4]:tw-font-semibold [&_h4]:tw-mb-4 [&_li]:tw-text-[#495057] [&_li]:tw-text-[0.95rem] [&_li]:tw-p-[0.5rem_0] [&_li]:tw-flex [&_li]:tw-items-center [&_li]:tw-gap-3 [&_.tw-text-slate-500]:!tw-text-[#6c757d] [&_.tw-text-slate-500]:tw-text-[1.1rem] [&_li:hover]:tw-bg-[#f1f3f5] [&_li:hover]:tw-rounded-[6px] [&_li:hover]:tw-pl-2 [&_li:hover]:[transition:all_0.2s_ease] course-includes"}>
                          <h4 className="h6 tw-mb-4">This course includes:</h4>
                          {hasModules ? (
                            <ul className="tw-list-none tw-pl-0">
                              <li className="tw-mb-2 tw-flex tw-items-center">
                                <Icon icon="material-symbols:menu-book" className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-me-2" />
                                {courseData?.courseMeta?.modulesCount || 0} Modules
                              </li>
                              <li className="tw-mb-2 tw-flex tw-items-center">
                                <FaVideo className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-me-2" />
                                {(courseData?.modules
                                  ? Object.values(courseData.modules).flat().filter((item) => item.type === 'Video').length
                                  : 0)} Videos
                              </li>
                              <li className="tw-mb-2 tw-flex tw-items-center">
                                <Icon icon="material-symbols:quiz" className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-me-2" />
                                {(courseData?.modules
                                  ? Object.values(courseData.modules).flat().filter((item) => item.type === 'Assessment').length
                                  : 0)} Assessments
                              </li>
                              <li className="tw-mb-2 tw-flex tw-items-center">
                                <Icon icon="material-symbols:description-outline" className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-me-2" />
                                {(courseData?.modules
                                  ? Object.values(courseData.modules).flat().filter((item) => item.type === 'Document').length
                                  : 0)} Documents
                              </li>
                              <li className="tw-mb-2 tw-flex tw-items-center">
                                <Icon icon="material-symbols:analytics-outline" className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-me-2" />
                                {(courseData?.modules
                                  ? Object.values(courseData.modules).flat().filter((item) => item.type === 'Survey').length
                                  : 0)} Surveys
                              </li>
                              {/* <li className="mb-2 d-flex align-items-center">
                                <Icon icon="material-symbols:group" className="text-muted me-2" />
                                {courseData?.courseMeta?.enrolledCount || 0} Total Enrollments
                              </li> */}
                              <li className="tw-mb-2 tw-flex tw-items-center">
                                <FaInfinity className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-me-2" />
                                Full lifetime access
                              </li>
                              <li className="tw-mb-2 tw-flex tw-items-center">
                                <FaCertificate className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-me-2" />
                                Certificate of completion
                              </li>
                            </ul>
                          ) : (
                            <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Course content is being prepared</p>
                          )}
                        </div>



                      </div>
                    </div>
                  </div>


                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default CourseDetails;
